import { useMemo } from "react";
import type { OrderTableRow } from "../types";
import { TableButtonEdit, TableButtonDelete, TableButtonDownload } from "./tableButton";

interface Props {
  /** テーブル表示用データ */
  datas?: OrderTableRow[];
  /** 編集ボタン押下時 */
  onEdit: (data: OrderTableRow) => void;
  /** 削除ボタン押下時 */
  onDelete: (data: OrderTableRow) => void;
  /** ダウンロードボタン押下時 */
  onDownload: (data: OrderTableRow) => void;
  /** 編集可否 */
  canEdit: boolean;
}

/**
 * 発注登録一覧・ダウンロード画面用テーブル
 * @param props
 * @returns
 */
export default function OrderTable({ datas, onEdit, onDelete, onDownload, canEdit }: Props) {
  const DATA_HEADERS = ["登録日", "会社名", "薬剤名", "納入予定日", "納入予定時刻", "数量", "単位"];
  const ACTION_HEADERS = ["変更", "削除", "発注書ダウンロード"];

  // データをグループ化し、各セルのrowspanを計算
  const processedData = useMemo(() => {
    const result: OrderTableRow[] = [];

    // 登録日でグループ化
    const dateGroups = datas?.reduce(
      (acc, item) => {
        if (!acc[item.orderAddDate]) {
          acc[item.orderAddDate] = [];
        }
        acc[item.orderAddDate].push(item);
        return acc;
      },
      {} as Record<string, OrderTableRow[]>
    );

    // 登録日ごとに会社をグループ化
    Object.entries(dateGroups ? dateGroups : "").forEach(
      ([date, dateItems]: [string, OrderTableRow[]]) => {
        const vendorGroups = dateItems.reduce(
          (acc: { [x: string]: any[] }, item: { orderAddDate: string; vendorId: number }) => {
            const key = `${item.orderAddDate}-${item.vendorId}`;
            if (!acc[key]) {
              acc[key] = [];
            }
            acc[key].push(item);
            return acc;
          },
          {} as Record<string, OrderTableRow[]>
        );

        // 登録日の行数（初めて見つかる登録日のフラグをtrueにしておく）
        const dateRowSpan = dateItems.length;
        let isFirstDateRow = true;

        // 登録日の会社ごとに表示データ作成
        Object.entries(vendorGroups).forEach(([vendorKey, vendorItems]) => {
          // 会社名の行数（初めて見つかる会社名のフラグをtrueにしておく）
          const vendorRowSpan = vendorItems.length;
          let isFirstVendorRow = true;

          vendorItems.forEach(
            (
              item: OrderTableRow & {
                dateRowSpan: number;
                vendorRowSpan: number;
                showDate: boolean;
                showVendor: boolean;
              }
            ) => {
              result.push({
                ...item,
                dateRowSpan,
                vendorRowSpan,
                showDate: isFirstDateRow,
                showVendor: isFirstVendorRow,
              });
              isFirstDateRow = false;
              isFirstVendorRow = false;
            }
          );
        });
      }
    );

    return result;
  }, [datas]);

  return (
    <>
      {datas?.length > 0 ? (
        <table className="orderlist-table">
          <thead>
            <tr>
              <th colSpan={DATA_HEADERS.length}>発注登録</th>
              <th colSpan={canEdit ? ACTION_HEADERS.length : 1}>データ操作</th>
            </tr>
            <tr>
              {DATA_HEADERS.map((header, index) => (
                <th key={`orderlist-${index}`}>{header}</th>
              ))}
              {ACTION_HEADERS.slice(canEdit ? -3 : -1).map((header, index) => (
                <th key={`orderlist-actions-${index}`}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {processedData.map((row, index) => (
              <tr key={index}>
                {row.showDate && <td rowSpan={row.dateRowSpan}>{row.orderAddDate}</td>}
                {row.showVendor && (
                  <td rowSpan={row.vendorRowSpan}>{row.vendorName || "不明な会社名"}</td>
                )}
                <td>{row.chemicalName || "不明な薬品名"}</td>
                <td>{row.scheduledDate}</td>
                <td>
                  {row.scheduledTime === "00:00" && row.scheduledTimeEnd === "00:00"
                    ? "指定なし"
                    : row.scheduledTime === "00:00" && row.scheduledTimeEnd === "12:00"
                    ? "AM"
                    : row.scheduledTime === "12:01" && row.scheduledTimeEnd === "23:59"
                    ? "PM"
                    : `${row.scheduledTime}${row.scheduledTimeEnd ? `〜${row.scheduledTimeEnd}` : ""}`}
                </td>
                <td>{row.orderVolume}</td>
                <td>{row.chemicalUnit2 || row.chemicalUnit1 || "不明な単位"}</td>

                {/* 変更 */}
                {canEdit && (
                  <td>
                    {!row.hiddenAt && <TableButtonEdit onClick={() => canEdit && onEdit(row)} />}
                  </td>
                )}

                {/* 削除 */}
                {canEdit && (
                  <td>
                    {!row.hiddenAt && (
                      <TableButtonDelete onClick={() => canEdit && onDelete(row)} />
                    )}
                  </td>
                )}

                {/* 発注書ダウンロード */}
                <td>
                  <TableButtonDownload onClick={() => onDownload(row)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p>選択された年月にデータが存在しません</p>
      )}
    </>
  );
}
